import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminSignIn, AdminNav } from "@/components/admin/AdminShell";
import { toCsv, downloadCsv } from "@/lib/csv-export";
import { getAuditLog } from "@/lib/audit.functions";
import type { AuditRow } from "@/lib/audit.server";

export const Route = createFileRoute("/admin/audit")({
  component: AdminAuditPage,
  head: () => ({
    meta: [
      { title: "Audit trail · SFN internal" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Internal SFN admin audit log." },
    ],
  }),
});

const EVENTS = [
  { key: "", label: "All events" },
  { key: "wallet_connect", label: "Wallet connect" },
  { key: "wallet_disconnect", label: "Wallet disconnect" },
  { key: "receipt_generated", label: "Receipt generated" },
  { key: "receipt_verified", label: "Receipt verified" },
  { key: "ledger_write", label: "Ledger write" },
  { key: "reconciliation_run", label: "Reconciliation run" },
];

const RANGES = [
  { days: 1, label: "24 hours" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 365, label: "1 year" },
];

function AdminAuditPage() {
  const run = useServerFn(getAuditLog);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [days, setDays] = useState(7);
  const [event, setEvent] = useState("");
  const [wallet, setWallet] = useState("");
  const [signature, setSignature] = useState("");
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setAuthed(Boolean(session)),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  const load = useCallback(() => {
    if (authed !== true) return;
    setLoading(true);
    setError(null);
    run({
      data: {
        days,
        event: event || null,
        wallet: wallet.trim() || null,
        signature: signature.trim() || null,
      },
    })
      .then((res) => setRows((res as { rows: AuditRow[] }).rows))
      .catch(() => setError("Could not load the audit trail."))
      .finally(() => setLoading(false));
  }, [authed, days, event, run, signature, wallet]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, days]);

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    const csv = toCsv([
      ["id", "created_at", "event", "actor", "wallet", "signature", "detail"],
      ...rows.map((r) => [
        r.id,
        r.created_at,
        r.event,
        r.actor ?? "",
        r.wallet ?? "",
        r.signature ?? "",
        JSON.stringify(r.detail ?? {}),
      ]),
    ]);
    downloadCsv(`sfn-audit-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success(`Exported ${rows.length} audit entries.`);
  };

  if (authed === false) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h1 className="font-display text-3xl font-medium tracking-tight">Audit trail</h1>
        <AdminSignIn note="Internal audit log — only readable by an authenticated account." />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <h1 className="font-display text-3xl font-medium tracking-tight">Admin audit trail</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Wallet connections, receipt generation and verification, ledger writes and reconciliation
        runs — searchable by wallet, signature and date range.
      </p>
      <AdminNav current="audit" />

      <div className="mt-6 flex flex-wrap items-end gap-2">
        <div className="inline-flex items-center gap-1 rounded-full glass p-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                days === r.days
                  ? "bg-white/[0.12] text-foreground"
                  : "text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <label className="text-xs text-muted-foreground">
          Event
          <select
            value={event}
            onChange={(e) => setEvent(e.target.value)}
            className="ml-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-foreground"
          >
            {EVENTS.map((e) => (
              <option key={e.key} value={e.key} className="bg-background">
                {e.label}
              </option>
            ))}
          </select>
        </label>

        <input
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          placeholder="Wallet address"
          aria-label="Filter by wallet address"
          className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-xs text-foreground"
        />
        <input
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="Transaction signature"
          aria-label="Filter by transaction signature"
          className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-xs text-foreground"
        />
        <button
          type="button"
          onClick={load}
          className="rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-foreground hover:bg-white/[0.06]"
        >
          Search
        </button>
        <button
          type="button"
          aria-label="Download the audit trail as a CSV file"
          onClick={exportCsv}
          className="rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-foreground hover:bg-white/[0.06]"
        >
          Download CSV
        </button>
      </div>

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="mt-8 text-sm text-coral">{error}</p>}

      {!loading && !error && (
        <div className="mt-8 overflow-x-auto rounded-2xl glass">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Wallet</th>
                <th className="px-4 py-3">Signature</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-muted-foreground">
                    No audit entries match these filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/[0.06] align-top">
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-semibold">{r.event}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.actor ?? "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {r.wallet ? `${r.wallet.slice(0, 4)}…${r.wallet.slice(-4)}` : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {r.signature ? `${r.signature.slice(0, 8)}…${r.signature.slice(-6)}` : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                    {JSON.stringify(r.detail ?? {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
